<template>
  <div>
    <slot name="pageHeader">
      <PageHeader :underline="headerUnderline">
        <PageTitle
          :pre-title="preTitle"
          :title="title"
          :extra-title="extraTitle"
          :under-title="underTitle"
        >
          <slot name="pageActions" />
        </PageTitle>

        <HeaderNav v-if="tabFilter">
          <b-nav-item
            v-for="(tab, key) in filteredTabOptions"
            :key="key"
            :active="
              $route.params.tab === tab.value ||
              (!$route.params.tab && tab.default)
            "
            @click="changeTab(tab)"
          >
            <b-badge v-if="tab.beforeTabTitle" variant="soft-danger" pill>
              {{ tab.beforeTabTitle }}
            </b-badge>

            {{ __(tab.label) }}

            <b-badge
              v-if="tab.count && parseInt(tab.count) > 0"
              :data-testid="`${tab.value}-count`"
              variant="soft-secondary"
              pill
            >
              {{ tab.count }}
            </b-badge>
          </b-nav-item>
        </HeaderNav>
      </PageHeader>
    </slot>

    <template v-if="$slots.pageHeader">
      <div>
        <TableTopNav v-if="tabFilter && !inlineFilters">
          <b-nav-item
            v-for="(tab, key) in filteredTabOptions"
            :key="key"
            link-classes="nav-link-sm"
            :active="
              $route.params.tab === tab.value ||
              (!$route.params.tab && tab.default)
            "
            @click="changeTab(tab)"
          >
            {{ __(tab.label) }}

            <b-badge
              v-if="tab.count && parseInt(tab.count) > 0"
              variant="soft-secondary"
              pill
            >
              {{ tab.count }}
            </b-badge>
          </b-nav-item>
        </TableTopNav>
      </div>
    </template>

    <DataList
      v-if="list"
      :ref="id"
      :fetch-callback="fetchCallback"
      :default-sort="defaultSort"
      :model="model"
      :filters="filters"
      :has-filters="hasFilters"
      :selectable="selectable"
      :selected-rows="selectedRows"
      :show-footer="showFooter"
      :custom-list-group="customListGroup"
      @filterResetCalled="resetFilters"
      @tabCountsUpdateCalled="updateTabCounts"
      @breadcrumbsUpdateCalled="updateBreadcrumbs"
      @rowSelected="rowSelected"
    >
      <template #tableBreadcrumbs>
        <FilesBreadcrumbs :prefix="breadcrumbsPrefix" :items="breadcrumbs" />
      </template>

      <template #tableHeaderActions>
        <slot name="tableHeaderActions" />
      </template>

      <template #filters>
        <slot name="filters" />
      </template>

      <template #actionsPopover>
        <slot name="actionsPopover" />
      </template>

      <template #icon="{ item }">
        <slot name="icon" :item="item" />
      </template>

      <template #iconBlock="{ item }">
        <slot name="iconBlock" :item="item" />
      </template>

      <template #badge="{ item }">
        <slot name="badge" :item="item" />
      </template>

      <template #label="{ item }">
        <slot name="label" :item="item" />
      </template>

      <template #description="{ item }">
        <slot name="description" :item="item" />
      </template>

      <template #actionButtons="{ item }">
        <slot name="actionButtons" :item="item" />
      </template>

      <template #afterItem="{ item }">
        <slot name="afterItem" :item="item" />
      </template>

      <template #childItem="{ item }">
        <slot name="childItem" :item="item" />
      </template>

      <template #listGroupItem="{ item }">
        <slot name="listGroupItem" :item="item" />
      </template>
    </DataList>

    <DataTable
      v-else
      :id="id"
      :ref="id"
      :columns="columns"
      :fetch-callback="fetchCallback"
      :model="model"
      :default-sort="defaultSort"
      :filters="filters"
      :has-filters="hasFilters"
      :inline-filters="inlineFilters"
      :has-row-actions="hasRowActions"
      :selectable="selectable"
      :selected-rows="selectedRows"
      :headless="headless"
      :action-popover-condition="actionPopoverCondition"
      :table-class="tableClass"
      :mutate-data="mutateData"
      :is-pagination-links-visible="isPaginationLinksVisible"
      :row-click-handler="rowClickHandler"
      @filterResetCalled="resetFilters"
      @tabCountsUpdateCalled="updateTabCounts"
      @rowSelected="rowSelected"
    >
      <template #actionsPopover>
        <slot name="actionsPopover" />
      </template>

      <template #filters>
        <slot name="filters" />
      </template>

      <template #actionButtons="{ item }">
        <slot name="actionButtons" :item="item" />
      </template>

      <template #tableHeaderActions>
        <slot name="tableHeaderActions" />
      </template>

      <template v-for="column in columns" #[column.column]="{ item }">
        <slot :name="column.column" :item="item" />
      </template>

      <template #afterItem="{ item }">
        <slot name="afterItem" :item="item" />
      </template>

      <template #childItem="{ item }">
        <slot name="childItem" :item="item" />
      </template>

      <template #tfoot="meta">
        <slot name="tfoot" :meta="meta" />
      </template>
    </DataTable>
  </div>
</template>

<script>
import HeaderNav from '@/General/HeaderNav.vue'
import DataList from '@/General/Tables/DataList.vue'
import DataTable from '@/General/Tables/DataTable.vue'
import { FILTER_TAB } from '@/General/Tables/FilterManager.js'
import FilesBreadcrumbs from '@/General/Tables/FilesBreadcrumbs.vue'
import FilesBreadcrumbsModel from '@/General/Tables/FilesBreadcrumbsModel.js'
import TableTopNav from '@/General/TableTopNav.vue'
import { v4 as uuidv4 } from 'uuid'
import beautifyNumberFormatter from '@/General/beautifyNumberFormatter.js'

export default {
  name: 'DataPage',

  components: { DataList, TableTopNav, HeaderNav, DataTable, FilesBreadcrumbs },

  props: {
    preTitle: {
      type: String,
      default: null,
    },

    list: {
      type: Boolean,
      default: false,
    },

    defaultSort: {
      type: String,
      default: '-created_at',
    },

    title: {
      type: String,
      default: null,
    },

    extraTitle: {
      type: String,
      default: null,
    },

    hasFilters: {
      type: Boolean,
      default: false,
    },

    filters: {
      type: Array,
      default: () => {
        return []
      },
    },

    inlineFilters: {
      type: Boolean,
      default: false,
    },
    isPaginationLinksVisible: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      default: uuidv4(),
    },

    model: {},

    columns: {
      type: Array,
      default: () => {
        return []
      },
    },

    fetchCallback: {
      type: Function,
      default: () => {},
    },

    hasRowActions: {
      type: Boolean,
      default: false,
    },

    selectable: {
      type: Boolean | Function,
      default: false,
    },

    selectedRows: {
      type: Array,
      default: () => {
        return []
      },
    },

    rowSelected: {
      type: Function,
      default: () => {},
    },

    rowClickHandler: {
      type: Function,
      default: null,
    },

    showFooter: {
      type: Boolean,
      default: true,
    },

    headless: {
      type: Boolean,
      default: false,
    },

    headerUnderline: {
      type: Boolean,
      default: true,
    },

    actionPopoverCondition: {
      type: Function,
      default: () => true,
    },

    tableClass: {
      type: String,
      default: '',
    },

    mutateData: {
      type: Function,
    },

    underTitle: {
      type: String,
      default: null,
    },

    customListGroup: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      breadcrumbs: [],
      breadcrumbsPrefix: null,
    }
  },

  computed: {
    tabFilter: function () {
      return _.find(this.filters, (filter) => filter.type === FILTER_TAB)
    },

    filteredTabOptions() {
      return this.tabFilter.options.filter((tab) => this.isTabVisible(tab))
    },
  },

  watch: {
    ['$route.params.tab']: {
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.changeTab(newValue, true)
          this.$refs[this.id].filterTable()
        }
      },
    },
  },

  created() {
    this.setInitialTabFilter()
  },

  methods: {
    isTabVisible(tab) {
      if (tab.hidden) {
        return false
      }

      return tab.permission
        ? this.authUser().permissions.includes(tab.permission)
        : true
    },

    setInitialTabFilter() {
      if (!this.tabFilter) {
        return
      }

      if (this.$route.params.tab) {
        this.changeTab(this.$route.params.tab, true)

        return
      }

      const filteredTabOptions = this.filteredTabOptions

      if (filteredTabOptions) {
        const defaultOption = _.find(
          filteredTabOptions,
          (option) => !!option.default
        )

        if (defaultOption) {
          this.changeTab(defaultOption.value, true)

          return
        }
      }

      if (!this.$route.params.tab && !!filteredTabOptions.length) {
        this.changeTab(filteredTabOptions[0].value)
      }
    },

    changeTab(tab, initial = false) {
      if (tab?.route) {
        this.$navigate(tab.route.name, tab.route.params)
        return
      }

      const tabName = tab?.value ? tab.value : tab

      const filterIndex = _.findIndex(
        this.filters,
        (filter) => filter.type === FILTER_TAB
      )
      this.$set(this.filters[filterIndex], 'value', tabName)

      if (!initial) {
        const { name } = this.$route
        this.$navigate(name, { tab: tabName }, this.$route.query)
      }
    },

    resetFilters() {
      _.each(this.filters, (filter) => {
        if (filter.type !== FILTER_TAB) {
          filter.value = null
        }
      })

      this.$emit('resetFilters')
    },

    updateTabCounts(tabCounts) {
      const filterIndex = _.findIndex(
        this.filters,
        (filter) => filter?.type === FILTER_TAB
      )
      if (this.filters[filterIndex] && this.filters[filterIndex]['options']) {
        _.each(tabCounts, (count, key) => {
          const dashKey = key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)

          const optionIndex = _.findIndex(
            this.filters[filterIndex]['options'],
            (option) => option.value === dashKey
          )

          if (
            typeof optionIndex === 'number' &&
            typeof this.filters[filterIndex]['options'][optionIndex] !==
              'undefined'
          ) {
            this.$set(
              this.filters[filterIndex]['options'][optionIndex],
              'count',
              beautifyNumberFormatter(count)
            )
          }
        })
      }
    },

    /**
     * @param {object} breadcrumbs
     */
    updateBreadcrumbs(breadcrumbs) {
      /**
       * One Drive documents
       */
      if (_.isObject(breadcrumbs)) {
        const filesBreadcrumbs = new FilesBreadcrumbsModel(breadcrumbs)
        this.breadcrumbs = filesBreadcrumbs.generateBreadcrumbs(
          this.$route.name
        )
        this.breadcrumbsPrefix = filesBreadcrumbs.getRemotePath()
      }
      /**
       * System documents
       */
      if (_.isArray(breadcrumbs)) {
        this.breadcrumbs = new FilesBreadcrumbsModel({
          items: breadcrumbs,
        }).generateBreadcrumbs(this.$route.name)
      }
    },

    refreshTable() {
      this.$refs[this.id].refreshTable()
    },

    filterTable() {
      this.$refs[this.id].filterTable()
    },

    refreshTableWithPath(path) {
      this.$refs[this.id].refreshTableWithPath(path)
    },

    expandTableRows() {
      this.$refs[this.id].expandTableRows()
    },

    collapseTableRows() {
      this.$refs[this.id].collapseTableRows()
    },
  },
}
</script>
<style lang="scss">
.d-flex {
  a:hover {
    i {
      color: #2b7be5;
    }
  }
}
</style>
